import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal } from '@angular/core';
import { HdContentLanguageService } from '../../../chart/bodygraph/hd-content-language.service';
import { hexagramBits } from '../../core/iching-core';
import { IChingHexagram, IChingMethodContent, IChingReading } from '../../core/iching.models';
import { HdIChingHexagramFigureComponent } from '../../figure/hd-iching-hexagram-figure.component';
import { HdIChingTrigramFigureComponent } from '../../figure/hd-iching-trigram-figure.component';
import { injectIChingTranslate } from '../iching-consult-translate';
import { IChingTrigramShiftRow, buildTrigramShift } from './iching-reading-derivations';

/**
 * Het omslaghexagram (C5): beide figuren dicht bij elkaar met ↔ ertussen
 * (keerzijde, geen uitkomst), elk met nummer en titel eronder om te
 * vergelijken. Daaronder beide trigrammen als visuele samenvatting (boven, dan
 * beneden, in de trigramkleuren van C1): het trigram dat blijft en het trigram
 * dat wisselt. Dan een korte lopende toelichting: welk trigram verandert en
 * waarom (daar liggen de bewegende lijnen), wat dat trigram voor je staat en
 * van welke kern naar welke het gaat, en een vaste slotzin. Het Oordeel van het
 * omslaghexagram achter een knop. Bewust geen lijnteksten: die horen bij het
 * gegooide hexagram. De titel van het gegooide hexagram komt via `hexagram`
 * (leeg zolang de naam verborgen is).
 */
@Component({
  selector: 'hd-iching-change',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdIChingHexagramFigureComponent, HdIChingTrigramFigureComponent],
  styles: `
    :host { display: block; font-size: 13.5px; line-height: 1.5; }
    /* Rij 1: figuren en pijl (pijl op de hoogte van de figuren), rij 2: nummer en titel; beide kolommen even breed, links tegen elkaar. */
    .compare {
      display: grid; grid-template-columns: minmax(0, 180px) auto minmax(0, 180px); justify-content: start;
      align-items: center; justify-items: center; column-gap: 28px; row-gap: 4px;
    }
    .caption { align-self: start; display: flex; flex-direction: column; align-items: center; gap: 2px; width: 100%; }
    .caption[data-caption='changed'] { grid-column: 3; }
    .number { font-weight: 700; }
    .arrow { font-size: 26px; line-height: 1; opacity: 0.7; }
    .title { font-size: 12.5px; text-align: center; text-wrap: balance; }
    .shifts { margin: 16px 0 0; display: flex; flex-direction: column; gap: 6px; }
    .shift { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .shift-label { min-width: 5.5em; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.7; }
    .shift-arrow { opacity: 0.7; }
    .explanation { margin: 10px 0 0; }
    .rule { margin: 12px 0 0; font-style: italic; }
    .toggle {
      margin-top: 12px; padding: 4px 12px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18));
      border-radius: var(--radius-md, 6px); background: transparent; color: inherit; font: inherit; font-size: 13px; cursor: pointer;
    }
    .judgement {
      margin-top: 8px; padding: 8px 12px; border-left: 2px solid var(--hd-iching-accent, rgba(212, 168, 105, 0.6));
    }
  `,
  template: `
    <div class="compare">
      <hd-iching-hexagram-figure data-figure="primary" [lines]="reading().lines" />
      <span class="arrow" aria-hidden="true">↔</span>
      <hd-iching-hexagram-figure data-figure="changed" [figure]="changedFigure()" />
      <div class="caption" data-caption="primary">
        <span class="number">#{{ reading().primary }}</span>
        @if (hexagram(); as h) {
          <span class="title">{{ h.title }}</span>
        }
      </div>
      <div class="caption" data-caption="changed">
        <span class="number">#{{ reading().changed }}</span>
        @if (changedHexagram(); as h) {
          <span class="title">{{ h.title }}</span>
        }
      </div>
    </div>

    @if (explanation(); as text) {
      <div class="shifts" role="group" [attr.aria-label]="t('humanDesign.iching.reading.trigramShift')">
        @for (row of rows(); track row.kind) {
          <div class="shift" [attr.data-shift]="row.kind" [attr.data-shift-state]="row.changes ? 'changes' : 'stays'">
            <span class="shift-label">
              {{ row.kind === 'upper' ? t('humanDesign.iching.reading.shiftUpper') : t('humanDesign.iching.reading.shiftLower') }}
            </span>
            <hd-iching-trigram-figure data-shift-figure="from" [trigram]="row.from" size="medium" [tone]="row.kind" />
            @if (row.changes) {
              <span>{{ row.fromName }}</span>
              <span class="shift-arrow">→</span>
              <hd-iching-trigram-figure data-shift-figure="to" [trigram]="row.to" size="medium" [tone]="row.kind" />
              <span>{{ row.toName }}</span>
            } @else {
              <span>{{ t('humanDesign.iching.reading.shiftStays', { name: row.fromName }) }}</span>
            }
          </div>
        }
      </div>
      <p class="explanation" data-test-id="iching-shift-explanation">{{ text }}</p>
    }

    @if (changedHexagram(); as h) {
      <button type="button" class="toggle" data-test-id="iching-changed-judgement" [attr.aria-expanded]="judgementOpen()"
        (click)="judgementOpen.set(!judgementOpen())">
        {{ t('humanDesign.iching.reading.changedJudgement') }}
      </button>
      @if (judgementOpen()) {
        <div class="judgement">
          @for (verse of h.text.judgement.text; track $index) {
            <div>{{ verse }}</div>
          }
        </div>
      }
    }

    @if (rule(); as r) {
      <p class="rule">{{ r }}</p>
    }
  `,
})
export class HdIChingChangeComponent {
  readonly reading = input.required<IChingReading>();
  readonly method = input.required<IChingMethodContent>();
  /** Het gegooide hexagram, voor zijn titel; leeg zolang de naam verborgen is. */
  readonly hexagram = input<IChingHexagram | undefined>(undefined);
  readonly changedHexagram = input<IChingHexagram | undefined>(undefined);

  protected readonly t = injectIChingTranslate();
  private readonly contentLang = inject(HdContentLanguageService);

  /** Dicht bij elke nieuwe omslag. */
  protected readonly judgementOpen = linkedSignal({ source: () => this.reading().changed, computation: () => false });

  protected readonly changedFigure = computed(() => {
    const changed = this.reading().changed;
    return changed === undefined ? undefined : hexagramBits(changed);
  });

  protected readonly rule = computed(() => this.method().steps.steps.find(step => step.id === 'change')?.rule);

  /** Trigramwissel en lopende toelichting, gedeeld met de Markdown-export (iching-reading-derivations). */
  private readonly shift = computed(() => buildTrigramShift(this.reading(), this.method(), this.contentLang.effectiveLang(), this.t));
  /** Beide trigrammen, boven eerst (zoals ze in de figuur liggen): blijft het, of wisselt het. */
  protected readonly rows = computed<readonly IChingTrigramShiftRow[]>(() => this.shift().rows);
  /**
   * De lopende toelichting in drie delen: welk trigram verandert en waarom (met
   * wat het andere blijft), de rol van het wisselende trigram met de kernen van
   * en naar (jij voor je omgeving), en de vaste slotzin. Leeg zolang niets wisselt.
   */
  protected readonly explanation = computed<string | undefined>(() => this.shift().explanation);
}
